/**
 * View-layer zoom input. This feeds the CAMERA only — never the simulation —
 * so manual zoom can't affect game logic or determinism. It is deliberately
 * NOT an InputSource (that boundary carries sim intents); it just accumulates a
 * multiplicative zoom delta from the mouse wheel (desktop) and two-finger pinch
 * (mobile), which the camera applies and clamps to its bounds.
 */
export class ZoomInput {
  private readonly target: HTMLElement;
  private readonly step: number;
  /** Accumulated multiplier since the last drain (1 = no change). */
  private pending = 1;
  /** Last two-finger distance during an active pinch (0 = no pinch). */
  private pinchDist = 0;
  private active = false;

  constructor(target: HTMLElement, step: number) {
    this.target = target;
    this.step = step;
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    this.target.addEventListener('wheel', this.onWheel, { passive: false });
    this.target.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.target.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.target.addEventListener('touchend', this.onTouchEnd);
    this.target.addEventListener('touchcancel', this.onTouchEnd);
  }

  stop(): void {
    this.active = false;
    this.target.removeEventListener('wheel', this.onWheel);
    this.target.removeEventListener('touchstart', this.onTouchStart);
    this.target.removeEventListener('touchmove', this.onTouchMove);
    this.target.removeEventListener('touchend', this.onTouchEnd);
    this.target.removeEventListener('touchcancel', this.onTouchEnd);
  }

  /** Return and reset the accumulated zoom multiplier since the last call. */
  drain(): number {
    const d = this.pending;
    this.pending = 1;
    return d;
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    // Scroll up (deltaY < 0) zooms in; scroll down zooms out.
    this.pending *= e.deltaY < 0 ? this.step : 1 / this.step;
  };

  private static distance(a: Touch, b: Touch): number {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  private onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length === 2) {
      this.pinchDist = ZoomInput.distance(e.touches[0], e.touches[1]);
    }
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (e.touches.length === 2) {
      e.preventDefault(); // stop the browser's own pinch-zoom
      const d = ZoomInput.distance(e.touches[0], e.touches[1]);
      if (this.pinchDist > 0 && d > 0) this.pending *= d / this.pinchDist;
      this.pinchDist = d;
    }
  };

  private onTouchEnd = (): void => {
    this.pinchDist = 0;
  };
}
